import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  HeatmapBucketSortDirection,
  HeatmapBucketStatField,
} from '../common/heatmap.helper';
import { cn } from '../common/utils';
import { Button } from '../components/actions/Button';
import DropdownSelector from '../components/actions/DropdownSelector';
import * as Icon from '../components/icons';
import { ScreenTitle } from '../components/layout/ScreenTitle';
import { ActionBar } from '../components/nav/ActionBar';
import { HelpIndex, HelpNavTo } from '../helpContext/help.default';

type HeatmapSortDirectionOption = {
  label: string;
  value: HeatmapBucketSortDirection;
  icon: React.FC;
};

function useHeatmapSortDirectionOptions(): Record<
  HeatmapBucketSortDirection,
  HeatmapSortDirectionOption
> {
  const { t } = useTranslation('app');

  const sortDirectionDropdownOptions: HeatmapSortDirectionOption[] = [
    { label: t`Lowest`, value: 'asc', icon: Icon.SortAscending },
    { label: t`Highest`, value: 'desc', icon: Icon.SortDescending },
  ];
  return _.keyBy(sortDirectionDropdownOptions, o => o.value) as Record<
    HeatmapBucketSortDirection,
    HeatmapSortDirectionOption
  >;
}

type HeatmapSortOption = {
  key: HeatmapBucketStatField;
  label: string;
  unitOfMeasure?: string;
};

export function useHeatmapSortOptions(
  hasStats: boolean,
): Record<HeatmapBucketStatField, HeatmapSortOption> {
  const { t } = useTranslation('app');

  const sortFieldOptions: HeatmapSortOption[] = [
    { key: null, label: t`Title`, unitOfMeasure: t`events` },
  ];

  if (hasStats) {
    sortFieldOptions.push(
      { key: 'linesCount', label: t`Lines`, unitOfMeasure: t`lines` },
      { key: 'itemsCount', label: t`Items`, unitOfMeasure: t`items` },
      {
        key: 'totalExistedLocations',
        label: t`Locations`,
        unitOfMeasure: t`locations`,
      },
    );
  }

  return _.keyBy(sortFieldOptions, o => o.key) as Record<
    HeatmapBucketStatField,
    HeatmapSortOption
  >;
}

export type HeatmapLegendPanelHeaderProps = {
  title: string;
  hasStats: boolean;

  sortBy: HeatmapBucketStatField;
  onSortByChange: (value: HeatmapBucketStatField) => void;

  sortDirection: HeatmapBucketSortDirection;
  onSortDirectionChange: (value: HeatmapBucketSortDirection) => void;

  allSelected: boolean;
  toggleSelectAll: () => void;

  showPercentage: boolean;
  onShowPercentageChange: (value: boolean) => void;

  showDistribution: boolean;
  onShowDistributionChange: (value: boolean) => void;

  helpNavTo?: HelpNavTo<HelpIndex>;
};

const HeatmapLegendPanelHeader: React.FC<
  HeatmapLegendPanelHeaderProps
> = props => {
  const { t } = useTranslation('app');

  const sortDirectionOptions = useHeatmapSortDirectionOptions();
  const sortOptions = useHeatmapSortOptions(props.hasStats);

  return (
    <>
      <ScreenTitle
        isSticky
        title={props.title}
        subtitle={t`Heatmaps`}
        helpNavTo={props.helpNavTo}
        truncate
      />
      <ActionBar
        transparent
        className={cn(
          'p-2',
          'bg-app-panel-light/40',
          'saturate-110 backdrop-blur',
          'xl:top-18 sticky top-16',
          'z-header_screen',
          'gap-2',
        )}
      >
        <fieldset className={cn('flex flex-1 items-center', 'w-full')}>
          <DropdownSelector
            className={cn('w-full text-xs')}
            value={props.sortBy}
            values={_.keys(sortOptions) as HeatmapBucketStatField[]}
            renderValue={sortBy => sortOptions[sortBy]?.label}
            onChange={sortBy => props.onSortByChange(sortBy)}
            light
            widthFull
          />
          <DropdownSelector
            className={cn('text-xs')}
            classNameValue={cn('!rounded-r', 'p-1 mx-px')}
            DropAlignRight
            value={props.sortDirection}
            values={
              _.keys(sortDirectionOptions) as HeatmapBucketSortDirection[]
            }
            renderValue={value => sortDirectionOptions[value].label}
            onChange={v => props.onSortDirectionChange(v)}
            light
            iconValue={sortDirectionOptions[props.sortDirection]?.icon}
            iconValueOnly
            iconClass="w-4 h-4"
          />
        </fieldset>

        <Button
          className={cn(
            'rounded border !p-1',
            props.showDistribution
              ? 'bg-menu/30 border-menu/20'
              : 'border-menu/50',
          )}
          buttonIcon={
            <Icon.DataStatsPie
              className={cn(
                'fill-current',
                props.showDistribution ? 'text-menu-text' : 'text-menu-text',
                'group-hover:opacity-50',
                'h-4 w-4',
              )}
            />
          }
          buttonSize="xs"
          isTransparent
          hasIconAfter
          onPress={() =>
            props.onShowDistributionChange(!props.showDistribution)
          }
        />
        <DropdownSelector
          classNameLabel="text-sm"
          onClick={e => e.stopPropagation()}
          DropAlignRight
          buttonTransparent
          vertical
          panelMode
          value={'...'}
          values={[
            props.allSelected ? t`Hide All Stats` : t`Show All Stats`,
            props.showPercentage ? t`Show as Counts` : t`Show as Percentages`,
            props.showDistribution ? t`Hide Charts` : t`Show Charts`,
          ]}
          onChange={value => {
            switch (value) {
              case t`Hide All Stats`:
              case t`Show All Stats`:
                props.toggleSelectAll();
                break;
              case t`Show as Counts`:
              case t`Show as Percentages`:
                props.onShowPercentageChange(!props.showPercentage);
                break;
              case t`Hide Charts`:
              case t`Show Charts`:
                props.onShowDistributionChange(!props.showDistribution);
                break;
            }
          }}
        />
      </ActionBar>
    </>
  );
};

export default HeatmapLegendPanelHeader;
